/**
 * relationship.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { AbstractControl, FormControl, FormGroup, Validators }
    from "@angular/forms";
import { RelationshipPartnerRequest } from "../server/messages/subjects/";
import { Identifiable } from "../support/types/aliases";
import { SubjectContextType } from "./subject-context-type";
import { SubjectReference } from "./subject-reference";

/**
 * A definition for a category of relationships. This will ultimately come from
 * the code-generated classes of the relationship rules built by SMEs.
 */
export interface AbstractRelationshipType
{
    name : string;
    displayName : string;
    sourceType : SubjectContextType;
    targetType : SubjectContextType;
}

export interface RelationshipType extends AbstractRelationshipType
{
    sourceRoles : Array<{role : Role, partners : Role[]}>;
    targetRoles : Array<{role : Role, partners : Role[]}>;
}

export interface RelationshipTypeData extends AbstractRelationshipType
{
    sourceRoles : Array<{role : string, partners : string[]}>;
    targetRoles : Array<{role : string, partners : string[]}>;
}

/**
 * A Role is a label for a single subject in a related pair, relative to a
 * particular RelationshipType. NOTE that the lemma of `name` is usually a noun
 * describing the subject A as a node, but the lemma of `displayName` is usually
 * a clause describing the edge A→B.
 */
export interface Role
{
    name : string;
    displayName : string;
    description? : string;
    partners : Role[];
}

/**
 * The shape of relationship data in JSON before being instantiated.
 */
export interface RelationshipData
{
    id? : number;
    relationshipType : string;
    sourceRole? : string;
    targetRole? : string;
    targetSummary? : {
        contextType : SubjectContextType,
        summary : any[]
    };
}

interface SubjectTyped
{
    type : SubjectContextType;
}

export class Relationship
{
    /**
     * A session-specific id for this half-relationship. NOTE that the server
     * models Relationships as pairs of *Relations*, which only encapsulate one
     * direction / role. Therefore this id cannot be reused to access the
     * "converse" relation.
     */
    public id : number;

    /**
     * The definition for the relationship type.
     */
    public type : RelationshipType;

    /**
     * The {@link Role} that this subject plays in the relationship. NOTE that
     * this is a subject-relative usage of "source"; it has no correlation with
     * the definition-relative "source" and "target".
     */
    public sourceRole : Role;

    public targetRole : Role;

    /**
     * Determine whether this half-relationship is parallel or antiparallel with
     * the source-target labelling of the relationship definition.
     */
    public get isOrientedWithDefinition () : boolean
    {
        return (this.source.type === this.type.sourceType);
    }

    /**
     * An array of TypedData summarizing the partner Subject.
     */
    public targetSummary : {
        contextType : SubjectContextType,
        summary : any[]
    };

    /**
     * Circular link back to the {@link Subject} this Relationship is for.
     */
    public source : Identifiable & SubjectTyped;

    constructor (
        data : RelationshipData,
        types : RelationshipType[] = [],
        roles : Role[] = [],
        source? : Identifiable & SubjectTyped
    )
    {
        this.id = data.id;
        this.targetSummary = data.targetSummary;
        this.source = source;

        // The incoming message data uses the (string) names to identify
        // role and relationship dfns, so map to the appropriate objects.
        this.type = types.find(
            (rt : RelationshipType) => rt.name === data.relationshipType);

        this.sourceRole = roles.find(
            (role : Role) => role.name === data.sourceRole);
        this.targetRole = roles.find(
            (role : Role) => role.name === data.targetRole);

        // if (this.isOrientedWithDefinition)
        // {
        //     this.availableRoles = this.type.sourceRoles;
        //     this.availablePartnerRoles = this.type.targetRoles;
        // }
        // else
        // {
        //     this.availableRoles = this.type.targetRoles;
        //     this.availablePartnerRoles = this.type.sourceRoles;
        // }
    }

    /**
     * Create a {@link SubjectReference} that encapsulates the partnered subject
     */
    public buildRef () : SubjectReference
    {
        return {
            id: this.id,
            host: this,
            type: "relationship",
            request: new RelationshipPartnerRequest(this.source.id, this.id),
            priority: 20
        };
    }

    // Placeholders for editor slots
    public editing? : boolean;
    public sourceRoleEdit? : AbstractControl;
    public targetRoleEdit? : AbstractControl;

    public startEditing () : void
    {
        this.editing = true;
        this.sourceRoleEdit = new FormControl(null, Validators.required);
        this.targetRoleEdit = new FormControl(null, Validators.required);

        this.sourceRoleEdit.valueChanges.subscribe((newRole : Role) => {
            const partnerOpts : Role[] = this.getAvailablePartnerRoles();
            if (!this.targetRoleEdit.value && partnerOpts.length === 1)
            {
                this.targetRoleEdit.setValue(partnerOpts[0]);
            }
        });
        this.targetRoleEdit.valueChanges.subscribe((newRole : Role) => {
            const selfOpts : Role[] = this.getAvailableRoles();
            if (!this.sourceRoleEdit.value && selfOpts.length === 1)
            {
                this.sourceRoleEdit.setValue(selfOpts[0]);
            }
        });

        const initialSelfOpts : Role[] = this.getAvailableRoles();
        if (initialSelfOpts.length === 1)
        {
            this.sourceRoleEdit.setValue(initialSelfOpts[0]);
        }
    }

    public cancelEditing () : void
    {
        this.sourceRoleEdit = null;
        this.targetRoleEdit = null;
        this.editing = false;
    }

    public get editsValid () : boolean
    {
        return (
            this.sourceRoleEdit && this.sourceRoleEdit.valid
            && this.targetRoleEdit && this.targetRoleEdit.valid
        );
    }

    public getAvailableRoles () : Role[]
    {
        if (this.targetRoleEdit && this.targetRoleEdit.value)
        {
            const partnerSelection : Role = this.targetRoleEdit.value;
            if (this.isOrientedWithDefinition)
            {
                return this.type.sourceRoles.find(
                    (r : {role : Role}) => r.role.name === partnerSelection.name
                ).partners;
            }
            else
            {
                return this.type.targetRoles.find(
                    (r : {role : Role}) => r.role.name === partnerSelection.name
                ).partners;
            }
        }
        if (this.isOrientedWithDefinition)
        {
            return this.type.sourceRoles.map(
                (role : {role : Role}) => role.role);
        }
        else
        {
            return this.type.targetRoles.map(
                (role : {role : Role}) => role.role);
        }
    }
    public getAvailablePartnerRoles () : Role[]
    {
        if (this.sourceRoleEdit && this.sourceRoleEdit.value)
        {
            const mySelection : Role = this.sourceRoleEdit.value;
            if (this.isOrientedWithDefinition)
            {
                return this.type.sourceRoles.find(
                    (r : {role : Role}) => r.role.name === mySelection.name
                ).partners;
            }
            else
            {
                return this.type.targetRoles.find(
                    (r : {role : Role}) => r.role.name === mySelection.name
                ).partners;
            }
        }
        if (this.isOrientedWithDefinition)
        {
            return this.type.targetRoles.map(
                (role : {role : Role}) => role.role);
        }
        else
        {
            return this.type.sourceRoles.map(
                (role : {role : Role}) => role.role);
        }
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/subjects/relationship.ts